import { z } from 'zod';
import {
  ApprovalStatus,
  AssetType,
  DominantColor,
  Id,
  Int64,
  JsonObject,
  Percent,
  PermissionMask,
  ReleaseStatus,
  RightsLicense,
  RightsStatus,
  CcVariant,
  Timestamp,
  UnitInterval,
  Uri,
  VersionState,
  atLeastOneField,
  cursorPage,
} from './common.js';
import { BoardRef, ThumbnailSet } from './boards.js';
import { AssetTag } from './tags.js';
import { CustomFieldValue, CustomFieldValueSet } from './customFields.js';
import { AssetVersion, MediaUrls } from './versions.js';
import { UploadSession, UploadSessionCreate } from './uploads.js';

/** Card-level fields for the masonry grid (03 §4, §7). */
export const AssetCard = z.object({
  id: Id,
  workspace_id: Id,
  type: AssetType,
  title: z.string(),
  aspect_ratio: z.number().positive(),
  width: z.int().nullable().optional(),
  height: z.int().nullable().optional(),
  duration_ms: z.int().nullable().optional(),
  size_bytes: Int64.nullable().optional(),
  has_audio: z.boolean().nullable().optional(),
  page_count: z.int().nullable().optional(),
  active_version_id: Id,
  version_state: VersionState,
  processing_progress: Percent.nullable().optional(),
  approval_status: ApprovalStatus,
  version_count: z.int().min(1),
  comment_count: z.int(),
  unresolved_comment_count: z.int().optional(),
  thumbnails: ThumbnailSet,
  sprite_sheet_url: Uri.nullable().optional(),
  sprite_index_url: Uri.nullable().optional(),
  preview_mp4_url: Uri.nullable().optional(),
  dominant_colors: z.array(DominantColor).max(5).optional(),
  home_board_id: Id.optional(),
  board_ids: z.array(Id).optional(),
  sort_order: z.int().nullable().optional(),
  added_at: Timestamp.nullable().optional(),
  created_by: Id.nullable().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
  deleted_at: Timestamp.nullable().optional(),
  /** Release 1 S7a: archived (hidden, not purged); distinct from `deleted_at`. */
  archived_at: Timestamp.nullable().optional(),
  // --- release2 (contract) --- G3 incomplete badge, C4 rights
  incomplete: z.boolean().optional(),
  rights_expires_at: Timestamp.nullable().optional(),
  rights_status: RightsStatus.optional(),
  /** release4/R (META-3): embargo — the card is not usable before this instant; the grid shows "Embargoed" while it is in the future. */
  rights_valid_from: Timestamp.nullable().optional(),
  // --- release3 (contract) --- DEL-1 internal-only flag (never served through share links)
  share_excluded: z.boolean().optional(),
  urls_expire_at: Timestamp.optional(),
});
export type AssetCard = z.infer<typeof AssetCard>;

/** Usage rights of an asset (Release 2 C4). Every key optional and nullable; null = not recorded. */
export const AssetRights = z.object({
  license: RightsLicense.nullable().optional(),
  credit_line: z.string().max(500).nullable().optional(),
  restrictions: z.string().max(4000).nullable().optional(),
  model_release: ReleaseStatus.nullable().optional(),
  property_release: ReleaseStatus.nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  expires_at: Timestamp.nullable().optional(),
  // --- release4/R --- META-3 record fields
  /** Embargo: not usable before this instant; share links hide the asset until then. */
  valid_from: Timestamp.nullable().optional(),
  copyright_owner: z.string().max(500).nullable().optional(),
  copyright_year: z.int().min(1000).max(9999).nullable().optional(),
  licensee: z.string().max(500).nullable().optional(),
  /** Only with `license = creative_commons` (422 otherwise). */
  cc_variant: CcVariant.nullable().optional(),
  /** Present in responses; ignored in requests. */
  status: RightsStatus.optional(),
}).superRefine((r, ctx) => {
  // release4/R: a CC variant needs the CC license in the same body (bulk merge included — send both)
  if (r.cc_variant != null && r.license !== 'creative_commons') ctx.addIssue({ code: 'custom', path: ['cc_variant'], message: 'cc_variant requires license = creative_commons' });
});
export type AssetRights = z.infer<typeof AssetRights>;

export const AssetCardPage = cursorPage(AssetCard);
export type AssetCardPage = z.infer<typeof AssetCardPage>;

export const AiTag = z.object({
  label: z.string(),
  confidence: UnitInterval,
});
export type AiTag = z.infer<typeof AiTag>;

/** Full asset detail (Info tab, 04 §1). */
export const Asset = AssetCard.extend({
  description: z.string().max(10000).nullable().optional(),
  active_version: AssetVersion,
  boards: z.array(BoardRef),
  tags: z.array(AssetTag),
  custom_field_values: z.array(CustomFieldValue),
  ai_tags: z.array(AiTag).optional(),
  ocr_text: z.string().nullable().optional(),
  transcript_id: Id.nullable().optional(),
  approval_changed_by: Id.nullable().optional(),
  approval_changed_at: Timestamp.nullable().optional(),
  duplicate_of_asset_id: Id.nullable().optional(),
  /** Release 1 F5: set on a soft-deleted asset merged into another. */
  merged_into_asset_id: Id.nullable().optional(),
  media: MediaUrls.optional(),
  // --- release2 (contract) --- G3 field requirements, C4 rights
  missing_field_ids: z.array(Id).optional(),
  required_field_ids: z.array(Id).optional(),
  hidden_field_ids: z.array(Id).optional(),
  rights: AssetRights.optional(),
  download_url: Uri.nullable().optional(),
  effective_mask: PermissionMask.optional(),
});
export type Asset = z.infer<typeof Asset>;

export const AssetCreate = z.object({
  upload: UploadSessionCreate,
  title: z.string().max(500).optional(),
  description: z.string().max(10000).optional(),
  tag_ids: z.array(Id).optional(),
});
export type AssetCreate = z.infer<typeof AssetCreate>;

export const AssetCreateResponse = z.object({
  asset: AssetCard,
  upload: UploadSession,
});
export type AssetCreateResponse = z.infer<typeof AssetCreateResponse>;

export const AssetUpdate = atLeastOneField(
  z.object({
    title: z.string().min(1).max(500).optional(),
    description: z.string().max(10000).nullable().optional(),
    approval_status: ApprovalStatus.optional(),
    /** Release 3 DEL-1 (EDIT_METADATA). */
    share_excluded: z.boolean().optional(),
    tag_ids: z.array(Id).optional(),
    custom_field_values: z.array(CustomFieldValueSet.extend({ field_id: Id })).optional(),
  }),
);
export type AssetUpdate = z.infer<typeof AssetUpdate>;

export const ApprovalSet = z.object({
  status: ApprovalStatus,
  note: z.string().max(2000).optional(),
});
export type ApprovalSet = z.infer<typeof ApprovalSet>;

export const BulkAssetActionKind = z.enum([
  'move_to_board',
  'copy_to_board',
  'add_tags',
  'remove_tags',
  'set_approval',
  'delete',
  'restore',
  'archive',
  'unarchive',
  'download_zip',
  'set_custom_field',
  // release3/B: `{value: boolean}` (EDIT_METADATA)
  'set_share_excluded',
  // release4/X (EXP-2): metadata CSV of the selection → `job_id` (kind `export_csv`); VIEW per asset
  'export_csv',
]);
export type BulkAssetActionKind = z.infer<typeof BulkAssetActionKind>;

export const BulkAssetAction = z.object({
  asset_ids: z.array(Id).min(1).max(5000),
  action: BulkAssetActionKind,
  params: JsonObject.optional(),
});
export type BulkAssetAction = z.infer<typeof BulkAssetAction>;

export const BulkAssetResult = z.object({
  succeeded: z.array(Id),
  failed: z.array(
    z.object({
      asset_id: Id,
      code: z.string(),
      message: z.string().optional(),
    }),
  ),
  job_id: Id.nullable().optional(),
});
export type BulkAssetResult = z.infer<typeof BulkAssetResult>;
